import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import {
    RestaurantMenu,
    Close,
    SubdirectoryArrowRight,
    CheckCircle,
    StickyNote2
} from "@mui/icons-material";
import { useMemo } from "react";
import { IProdutoPedido } from "../../../pedidos/interface/IProdutoPedido";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { TipoProduto } from "../../enum/TipoProduto";
import { TipoItemProduto } from "../../enum/TipoItemProduto";

interface CardComboCarrinhoOperacaoProps {
    comboDescricao?: string;
    items: IProdutoPedido[];
    removerCombo: () => void;
}

const ItemCombo = ({ item }: { item: IProdutoPedido }) => {
    const { real } = useMoeda();

    const opcoes = useMemo(
        () => item.itens?.filter(i => i.tipo !== TipoItemProduto.observacao) ?? [],
        [item.itens]
    );

    const observacao = useMemo(() => {
        if (item.observacao) {
            return item.observacao;
        }
        return item.itens?.find(i => i.tipo === TipoItemProduto.observacao)?.descricao;
    }, [item]);

    const imagem = item.imagens?.length ? item.imagens[0]?.url : undefined;
    const isIndustrializado =
        item.tipo === TipoProduto.industrializado ||
        item.tipoProdutoReferencia === TipoProduto.industrializado;

    const lineTotal =
        ((item.preco ?? 0) +
            (item.itens?.reduce((soma, i) => soma + (i.preco ?? 0) * (i.quantidade ?? 1), 0) ?? 0)) *
        (item.quantidade ?? 1);

    return (
        <Box>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "action.hover",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    {imagem ? (
                        <Box
                            component="img"
                            src={imagem}
                            alt={item.descricao}
                            sx={{
                                width: isIndustrializado ? "70%" : "100%",
                                height: isIndustrializado ? "70%" : "100%",
                                objectFit: isIndustrializado ? "contain" : "cover"
                            }}
                        />
                    ) : (
                        <RestaurantMenu sx={{ color: "text.disabled" }} />
                    )}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                        <Typography fontSize={14} fontWeight={700}>
                            {item.quantidade ?? 1}x {item.descricao}
                        </Typography>
                        <Typography fontSize={14} fontWeight={700} whiteSpace="nowrap">
                            {real(lineTotal)}
                        </Typography>
                    </Box>

                    {opcoes.length > 0 ? (
                        <Stack spacing={0.5} mt={0.5}>
                            {opcoes.map((opcao, index) => (
                                <Stack
                                    key={opcao.uuid ?? `${opcao.descricao}-${index}`}
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                    sx={{
                                        pl: 1,
                                        borderLeft: "2px solid",
                                        borderColor: "divider"
                                    }}
                                >
                                    <SubdirectoryArrowRight
                                        sx={{ fontSize: 16, color: "text.disabled", mt: "2px" }}
                                    />
                                    <Box
                                        sx={{
                                            flex: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            gap: 1
                                        }}
                                    >
                                        <Typography fontSize={13}>{opcao.descricao}</Typography>
                                        {opcao.preco > 0 && (
                                            <Typography fontSize={12} color="text.secondary" whiteSpace="nowrap">
                                                +{real(opcao.preco * (opcao.quantidade ?? 1))}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
                            <CheckCircle sx={{ fontSize: 14, color: "primary.main" }} />
                            <Typography fontSize={12} color="primary.main">
                                Incluso no combo
                            </Typography>
                        </Stack>
                    )}

                    {observacao && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{
                                mt: 1,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: "action.hover"
                            }}
                        >
                            <StickyNote2 sx={{ fontSize: 16, color: "text.secondary", mt: "1px" }} />
                            <Typography fontSize={12} color="text.secondary" fontStyle="italic">
                                {observacao}
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export const CardComboCarrinhoOperacao = ({ comboDescricao, items, removerCombo }: CardComboCarrinhoOperacaoProps) => {
    const { real } = useMoeda();

    const total = useMemo(() => {
        return items.reduce((acc, item) => {
            let valorItem = item.preco ?? 0;
            item.itens?.forEach(i => {
                valorItem += (i.preco ?? 0) * (i.quantidade ?? 1);
            });
            return acc + valorItem * (item.quantidade ?? 1);
        }, 0);
    }, [items]);

    return (
        <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden", height: "100%" }}
        >
            <Box
                sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    px: 2,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <RestaurantMenu fontSize="small" />
                    <Typography fontWeight={700} noWrap title={comboDescricao}>
                        {comboDescricao ?? "Combo"}
                    </Typography>
                </Stack>
                <IconButton
                    size="small"
                    onClick={removerCombo}
                    sx={{
                        color: "primary.contrastText",
                        bgcolor: "rgba(255,255,255,0.15)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            <Stack spacing={2} sx={{ p: 2 }}>
                {items.map(item => (
                    <ItemCombo key={item.uuid} item={item} />
                ))}
            </Stack>

            <Box
                sx={{
                    bgcolor: "action.hover",
                    px: 2,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >
                <Typography fontSize={12} fontWeight={700} color="text.secondary" letterSpacing="0.05em">
                    TOTAL DO ITEM
                </Typography>
                <Typography fontSize={18} fontWeight={700} color="primary.main">
                    {real(total)}
                </Typography>
            </Box>
        </Paper>
    );
};
